import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/apiClient";
import { Sparkle } from "@phosphor-icons/react";

export default function Register() {
  const { t } = useTranslation();
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    is_disabled: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) {
      setError(t("register.pwMin"));
      return;
    }
    setLoading(true);
    try {
      await register(form);
      toast.success(t("register.welcome"));
      nav("/dashboard");
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="register-page" className="min-h-[80vh] grid grid-cols-1 lg:grid-cols-5 text-white">
      <div className="lg:col-span-2 flex items-center justify-center px-6 py-16 order-2 lg:order-1">
        <div className="w-full max-w-md">
          <div className="overline mb-3">{t("register.overline")}</div>
          <h1 className="font-display text-4xl mb-2">{t("register.title")}</h1>
          <p className="text-neutral-400 mb-10 text-sm">
            {t("register.subtitle")}
          </p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="block text-xs uppercase tracking-wider text-neutral-400 mb-2">
                {t("register.name")}
              </label>
              <input
                data-testid="register-name"
                value={form.name}
                onChange={set("name")}
                required
                className="w-full bg-black border border-white/20 text-white px-4 py-3 focus:border-[#E6C998] focus:outline-none focus:ring-1 focus:ring-[#E6C998]/40"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-neutral-400 mb-2">
                {t("register.email")}
              </label>
              <input
                data-testid="register-email"
                type="email"
                value={form.email}
                onChange={set("email")}
                required
                className="w-full bg-black border border-white/20 text-white px-4 py-3 focus:border-[#E6C998] focus:outline-none focus:ring-1 focus:ring-[#E6C998]/40"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-neutral-400 mb-2">
                {t("register.password")}
              </label>
              <input
                data-testid="register-password"
                type="password"
                value={form.password}
                onChange={set("password")}
                required
                minLength={6}
                className="w-full bg-black border border-white/20 text-white px-4 py-3 focus:border-[#E6C998] focus:outline-none focus:ring-1 focus:ring-[#E6C998]/40"
              />
            </div>

            <label
              data-testid="register-inclusion-toggle"
              className="flex items-start gap-3 p-4 border border-[#E6C998]/20 bg-[#141414] cursor-pointer"
            >
              <input
                type="checkbox"
                checked={form.is_disabled}
                onChange={set("is_disabled")}
                className="mt-1 accent-[#E6C998]"
              />
              <div>
                <div className="text-[#E6C998] text-sm font-medium flex items-center gap-2">
                  <Sparkle size={16} weight="duotone" />
                  {t("register.inclusionLabel")}
                </div>
                <div className="text-xs text-neutral-400 mt-1">
                  {t("register.inclusionHint")}
                </div>
              </div>
            </label>

            {error && (
              <div data-testid="register-error" className="text-sm text-red-400">
                {error}
              </div>
            )}

            <button
              type="submit"
              data-testid="register-submit"
              disabled={loading}
              className="btn-gold w-full py-4 rounded-none text-sm disabled:opacity-60"
            >
              {loading ? t("register.submitting") : t("register.submit")}
            </button>

            <p className="text-xs text-neutral-500 leading-relaxed">
              {t("register.termsLine")}{" "}
              <Link to="/terms" className="text-[#E6C998]">{t("register.termsLink")}</Link> {t("register.andWord")}{" "}
              <Link to="/privacy" className="text-[#E6C998]">{t("register.privacyLink")}</Link>.
            </p>
          </form>

          <div className="divider-gold my-10" />

          <div className="text-sm text-neutral-400">
            {t("register.already")}{" "}
            <Link to="/login" className="text-[#E6C998] gold-underline">
              {t("register.signIn")}
            </Link>
          </div>
        </div>
      </div>
      <div className="lg:col-span-3 relative border-l border-white/10 order-1 lg:order-2">
        <img
          src="https://images.unsplash.com/photo-1786041176778-14825b13576f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBkYXJrfGVufDB8fHx8MTc4NzYxNzkzMHww&ixlib=rb-4.1.0&q=85"
          alt="Reserve"
          className="w-full h-full object-cover opacity-40 min-h-[300px]"
        />
        <div className="absolute inset-0 bg-gradient-to-l from-[#050505] via-[#050505]/50 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 lg:right-auto">
          <div className="overline mb-3">{t("register.heroOverline")}</div>
          <div className="font-display text-3xl lg:text-5xl max-w-lg leading-tight">
            {t("register.heroTitle")}
          </div>
        </div>
      </div>
    </div>
  );
}
